import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../firebase';

/**
 * "Add a track" (2026-09-26). The teacher's half of the track-job queue:
 * upload the file under the student's portal folder, then write ONE
 * `trackJobs` doc. Everything after that happens elsewhere — a worker claims
 * a vocals-on job and writes the vocals-out result, and the fileTrackJob
 * trigger writes the studentTracks pointer the strip reads. The queue's rules
 * and timing live in functions/trackJobs.js; the Firestore and Storage rules
 * pin the path shape below (`tracks/_portal/<studentId>/...`).
 *
 * The job id is minted BEFORE the upload so the file's path carries it: one
 * folder per job, nothing to collide with, and the worker writes its result
 * beside the source.
 */

const COLLECTION = 'trackJobs';
export const PORTAL_TRACKS_PREFIX = 'tracks/_portal';
// Mirrors MAX_UPLOAD_BYTES in functions/trackJobs.js and the storage rule.
export const MAX_TRACK_BYTES = 40 * 1024 * 1024;

/** A file name safe for a Storage path: the original's words, no slashes. */
export const safeFileName = (name) => {
	const s = String(name || 'track')
		.normalize('NFKD')
		.replace(/[^\w.\- ()]+/g, '')
		.replace(/\s+/g, ' ')
		.trim();
	return s || 'track';
};

/**
 * Words in a file name that say the vocals are ALREADY out: a Karaoke Version
 * download, an instrumental, a backing track. The toggle defaults OFF for
 * those and ON for everything else (a ClipGrab rip of the recording). Only a
 * default — the teacher can flip it before adding.
 */
const ALREADY_INSTRUMENTAL = /\b(karaoke|instrumental|backing|accompaniment|minus[\s-]?one|no[\s-]?vocals?|playback|custom[\s-]?backing)\b/i;
export const defaultRemoveVocals = (fileName) => !ALREADY_INSTRUMENTAL.test(String(fileName || ''));

/** Duration in seconds from the file's own metadata, or null. Used only for
 *  the Length word (Full vs Cut), so a failure is harmless. */
export const probeDurationSec = (file, timeoutMs = 5000) =>
	new Promise((resolve) => {
		if (typeof window === 'undefined' || typeof Audio === 'undefined' || !window.URL?.createObjectURL) {
			resolve(null);
			return;
		}
		let url = null;
		let done = false;
		const finish = (v) => {
			if (done) return;
			done = true;
			if (url) window.URL.revokeObjectURL(url);
			resolve(Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : null);
		};
		try {
			url = window.URL.createObjectURL(file);
			const a = new Audio();
			a.preload = 'metadata';
			a.onloadedmetadata = () => finish(a.duration);
			a.onerror = () => finish(null);
			a.src = url;
			setTimeout(() => finish(null), timeoutMs);
		} catch (e) {
			finish(null);
		}
	});

/**
 * Upload + queue. `onProgress(0..1)` reports the upload. Resolves with the
 * job id once the job doc exists; the caller watches the job from there.
 */
export const addTrackForStudent = async ({ file, studentId, studentName = null, song, removeVocals, uid, onProgress }) => {
	if (!file) throw new Error('Choose a file first.');
	if (!studentId) throw new Error('This lesson has no student linked.');
	if (!song || !String(song.name || '').trim()) throw new Error('Open a song tab first.');
	if (file.size > MAX_TRACK_BYTES) throw new Error('That file is over 40 MB. Add one song or one cut at a time.');
	if (file.type && !/^audio\//.test(file.type)) throw new Error('That is not an audio file.');

	const jobRef = doc(collection(db, COLLECTION));
	const fileName = safeFileName(file.name);
	const storagePath = `${PORTAL_TRACKS_PREFIX}/${studentId}/${jobRef.id}/${fileName}`;
	const contentType = file.type || 'audio/mpeg';
	// Read the duration while the bytes upload; it only picks the Length word.
	const durationPromise = probeDurationSec(file);

	const task = uploadBytesResumable(ref(storage, storagePath), file, { contentType });
	await new Promise((resolve, reject) => {
		task.on(
			'state_changed',
			(snap) => {
				if (onProgress && snap.totalBytes > 0) onProgress(snap.bytesTransferred / snap.totalBytes);
			},
			reject,
			resolve
		);
	});
	const storageUrl = await getDownloadURL(task.snapshot.ref);
	const durationSec = await durationPromise;

	await setDoc(jobRef, {
		studentId,
		studentName,
		songId: song.canonicalSongId || song.songId || null,
		songName: String(song.name).trim(),
		removeVocals: Boolean(removeVocals),
		source: { storagePath, storageUrl, fileName: file.name || fileName, contentType, sizeBytes: file.size || null, durationSec },
		status: 'queued',
		createdBy: uid,
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	});
	return jobRef.id;
};

/** A link to ONE YouTube video that a studio Mac can fetch. A search results
 *  page or a playlist is refused: yt-dlp treats either as a list and
 *  `--no-playlist` does not stop it, so a pasted search page ran for 39
 *  minutes and then failed because two of its results were unavailable
 *  (Dani Sandgren's "Black Sheep", 2026-10-27 and 11-03). Narrower than
 *  LINK_PATTERN and the trackJobs rule, which accept any YouTube address; the
 *  helper's own check is videoIdOf in functions/trackJobs.js. */
const SINGLE_VIDEO_LINK = /^https:\/\/(?:(?:www\.|m\.|music\.)?youtube\.com\/(?:watch\?(?:\S*&)?v=|embed\/|shorts\/|live\/)|(?:www\.)?youtu\.be\/)[A-Za-z0-9_-]{6,20}(?:[?&#/]\S*)?$/;
export const isYouTubeLink = (url) =>
	typeof url === 'string' && url.trim().length <= 500 && SINGLE_VIDEO_LINK.test(url.trim());

/** The link a SAVED song video (the lyrics / karaoke / performance slots)
 *  becomes a track from: kept as saved when it already passes isYouTubeLink,
 *  else rebuilt from its video id (an http:// or /embed/ save), else null. One
 *  function for both the video dock and the Add a track panel, so the same
 *  video is the same link wherever it is tapped (the double-tap guard keys
 *  on it). */
export const linkForSavedVideo = (url) => {
	const u = String(url || '').trim();
	if (isYouTubeLink(u)) return u;
	const m = u.match(/(?:youtube\.com\/(?:watch\?(?:\S*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/);
	return m ? `https://www.youtube.com/watch?v=${m[1]}` : null;
};

/**
 * "Paste a link" (2026-09-27): no upload. The job carries the link; a studio
 * Mac downloads it, files the recording, and removes the vocals if asked
 * (functions/trackJobs.js -> isLink). The cloud never takes one.
 */
export const addTrackFromLink = async ({ url, studentId, studentName = null, song, removeVocals, uid }) => {
	const link = String(url || '').trim();
	if (!isYouTubeLink(link)) throw new Error('Paste a link to one YouTube video (youtube.com or youtu.be).');
	if (!studentId) throw new Error('This lesson has no student linked.');
	if (!song || !String(song.name || '').trim()) throw new Error('Open a song tab first.');
	const jobRef = doc(collection(db, COLLECTION));
	await setDoc(jobRef, {
		kind: 'link',
		studentId,
		studentName,
		songId: song.canonicalSongId || song.songId || null,
		songName: String(song.name).trim(),
		removeVocals: Boolean(removeVocals),
		source: { url: link },
		status: 'queued',
		createdBy: uid,
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	});
	return jobRef.id;
};

/**
 * "Save as the student's key" (build step 4): ONE job per decision, never
 * per tap (Anthony: trying keys in a lesson renders nothing). `plan` comes
 * from keySaveFor, so the source is always the original recording.
 */
export const saveStudentKey = async ({ studentId, studentName = null, song, plan, uid }) => {
	if (!studentId || !song || !plan || !plan.source) throw new Error('Nothing to save.');
	const jobRef = doc(collection(db, COLLECTION));
	const src = plan.source;
	await setDoc(jobRef, {
		kind: 'transpose',
		studentId,
		studentName,
		songId: song.canonicalSongId || song.songId || null,
		songName: String(song.name).trim(),
		removeVocals: false,
		semitones: plan.semitones,
		source: {
			storagePath: src.storagePath,
			storageUrl: src.storageUrl,
			fileName: src.name || null,
			variant: src.variant || 'Track',
			length: src.length || null,
			durationSec: src.durationSec || null,
		},
		status: 'queued',
		createdBy: uid,
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	});
	return jobRef.id;
};

/**
 * "Save as a cut" (2026-09-28): one job per file in `set` (cutSetFor), all
 * sharing a `group` id that becomes the cut files' jobId, written in one
 * batch so a set is never half-queued. `lyricsOf(file)` supplies each file's
 * timed words; the filing trigger shifts them onto the cut's clock.
 */
export const saveTrackCut = async ({ studentId, studentName = null, song, set, start, end, uid, lyricsOf = (f) => f.lyrics || null }) => {
	if (!studentId || !song || !Array.isArray(set) || !set.length) throw new Error('Nothing to cut.');
	const s0 = Math.round(Number(start) * 10) / 10;
	const e0 = Math.round(Number(end) * 10) / 10;
	if (!(e0 - s0 >= 5)) throw new Error('A cut has to be at least 5 seconds long.');
	const batch = writeBatch(db);
	const group = doc(collection(db, COLLECTION)).id;
	const ids = [];
	for (const f of set) {
		const ref = doc(collection(db, COLLECTION));
		const lyr = lyricsOf(f);
		batch.set(ref, {
			kind: 'cut',
			group,
			studentId,
			studentName,
			songId: song.canonicalSongId || song.songId || null,
			songName: String(song.name).trim(),
			removeVocals: false,
			start: s0,
			end: e0,
			source: {
				storagePath: f.storagePath,
				storageUrl: f.storageUrl || null,
				fileName: f.name || null,
				variant: f.variant || 'Track',
				key: f.key || null,
				durationSec: f.durationSec || null,
				lyrics: lyr && Array.isArray(lyr.timed) ? { timed: lyr.timed, method: lyr.method || null, unverified: Boolean(lyr.unverified) } : null,
			},
			status: 'queued',
			createdBy: uid,
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp(),
		});
		ids.push(ref.id);
	}
	await batch.commit();
	return ids;
};

/** Live jobs for one student (one equality filter: no composite index). The
 *  caller filters to the ones worth showing. */
export const subscribeToStudentTrackJobs = (studentId, onChange, onError) => {
	if (!studentId) {
		onChange([]);
		return () => {};
	}
	return onSnapshot(
		query(collection(db, COLLECTION), where('studentId', '==', studentId)),
		(snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('Error watching track jobs:', err);
			if (onError) onError(err);
		}
	);
};

/** Every worker's check-in doc, live (build step 2). A handful of docs, one
 *  per studio Mac, so the whole collection is the query. */
export const subscribeToTrackWorkers = (onChange, onError) =>
	onSnapshot(
		collection(db, 'trackWorkers'),
		(snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('Error watching track workers:', err);
			if (onError) onError(err);
		}
	);

/** Stop a job a teacher started by mistake (2026-10-02). The callable
 *  marks it cancelled, which stops the studio Mac, and deletes everything the
 *  job wrote to Storage (functions/index.js cancelTrackJob). */
export const stopTrackJob = async (jobId) => {
	const res = await httpsCallable(functions, 'cancelTrackJob')({ jobId });
	return res.data;
};
